/**
 * Phase 0 lawyer-tester pipeline transparency client (pirx-2uo2o / pirx-42lxr).
 *
 * pirx-cvwv2.5: the 7 envelope shapes below (previously hand-retyped here and
 * separately in the backend service, with nothing checking the two sides
 * agree) are now sourced from the shared zod schemas in
 * `@vardic/shared/pipeline-transparency.ts` (pirx-cvwv2.3). Each function
 * validates its response through the matching schema and reports drift via
 * `console.warn` — log + fall-through, never throw, matching the existing
 * `packages/shared/src/api-client/response-schemas.ts` convention (see e.g.
 * `getAudit`'s `validateResponse` call) — so a real backend/frontend shape
 * mismatch becomes observable instead of silently masked by a bare cast.
 */

import { request } from "./request";
import {
  llmInteractionPromptArchiveResultSchema,
  pipelineRunCostResultSchema,
  pipelineRunEventsResultSchema,
  pipelineRunFeedbackResultSchema,
  pipelineTransparencyResultSchema,
  type AuditFailureAttributionWire,
  type LlmInteractionPromptArchiveResult,
  type PipelineRunCostCallWire,
  type PipelineRunCostResultWire,
  type PipelineRunEventsResultWire,
  type PipelineRunFeedbackResultWire,
  type PipelineTransparencyFeedbackWire,
  type PipelineTransparencyInteractionWire,
  type PipelineTransparencyResultWire,
} from "@vardic/shared/pipeline-transparency.ts";

export type {
  DynamicSpanView,
  LlmInteractionPromptArchiveResult,
  PipelineRagChunksBlob,
  PipelineStepEvent,
  PipelineStepEventLlmInteractionView,
  PipelineTransparencyAuditContextSummary,
  PipelineTransparencyCsrSummary,
  RagRawCandidateRow,
} from "@vardic/shared/pipeline-transparency.ts";
import type { RagRawCandidateRow } from "@vardic/shared/pipeline-transparency.ts";

// The 7 envelope shapes, re-exported under their original names so every
// existing consumer (ClaimView, TimingView, PipelineTransparency, the
// polling hooks) keeps importing from this file unchanged.
export type PipelineTransparencyInteraction =
  PipelineTransparencyInteractionWire;
export type PipelineTransparencyFeedback = PipelineTransparencyFeedbackWire;
export type PipelineTransparencyResult = PipelineTransparencyResultWire;
export type AuditFailureAttribution = AuditFailureAttributionWire;
export type PipelineRunEventsResult = PipelineRunEventsResultWire;
export type PipelineRunFeedbackResult = PipelineRunFeedbackResultWire;
export type PipelineRunCostCall = PipelineRunCostCallWire;
export type PipelineRunCostResult = PipelineRunCostResultWire;

/**
 * Log + fall-through: report a response schema mismatch without throwing or
 * altering the payload — same defensive semantics as the shared
 * `packages/shared/src/api-client/response-schemas.ts#validateResponse`
 * helper (this file can't import it directly: it isn't part of that
 * package's public `./api-client` export surface), applied locally to this
 * client's own 7 envelope schemas.
 */
function warnOnSchemaDrift(
  context: string,
  result: { success: boolean; error?: { issues: unknown } },
): void {
  if (!result.success) {
    console.warn(
      `[pipeline-transparency-client] response schema drift at ${context}:`,
      result.error?.issues,
    );
  }
}

export async function getPipelineTransparency(
  auditId: string,
): Promise<PipelineTransparencyResult> {
  const data = await request<PipelineTransparencyResult>(
    `/internal/pipeline-transparency/${auditId}`,
  );
  warnOnSchemaDrift(
    "getPipelineTransparency",
    pipelineTransparencyResultSchema.safeParse(data),
  );
  return data;
}

// ---------------------------------------------------------------------------
// Phase 1 (pirx-0zzkg): pipeline-runs polling endpoints (pirx-ee7ou).
//
// PipelineStepEvent / PipelineRagChunksBlob / PipelineStepEventLlmInteractionView
// are re-exported above from @vardic/shared/pipeline-transparency.ts
// (pirx-4wg3x.1) — single source of truth shared with the backend.
// ---------------------------------------------------------------------------

export async function getPipelineRunEvents(
  runId: string,
): Promise<PipelineRunEventsResult> {
  const data = await request<PipelineRunEventsResult>(
    `/internal/pipeline-runs/${runId}/events`,
  );
  warnOnSchemaDrift(
    "getPipelineRunEvents",
    pipelineRunEventsResultSchema.safeParse(data),
  );
  return data;
}

export async function getPipelineRunFeedback(
  runId: string,
): Promise<PipelineRunFeedbackResult> {
  const data = await request<PipelineRunFeedbackResult>(
    `/internal/pipeline-runs/${runId}/feedback`,
  );
  warnOnSchemaDrift(
    "getPipelineRunFeedback",
    pipelineRunFeedbackResultSchema.safeParse(data),
  );
  return data;
}

// ---------------------------------------------------------------------------
// pirx-vo6sw: per-call cost, read live from Langfuse (no DB storage).
// ---------------------------------------------------------------------------

export async function getPipelineRunCost(
  runId: string,
): Promise<PipelineRunCostResult> {
  const data = await request<PipelineRunCostResult>(
    `/internal/pipeline-runs/${runId}/cost`,
  );
  warnOnSchemaDrift(
    "getPipelineRunCost",
    pipelineRunCostResultSchema.safeParse(data),
  );
  return data;
}

// ---------------------------------------------------------------------------
// pirx-dnn42.4: raw pre-rank RAG candidates, on demand only.
// ---------------------------------------------------------------------------

interface PipelineStepEventRagRawCandidatesResult {
  stepEventId: string;
  candidates: RagRawCandidateRow[];
}

/**
 * The ~80-120 raw pre-rank candidates for one step event. Call this ONLY from
 * a tester's expand handler — never on mount and never from a poll tick: this
 * payload is excluded from `getPipelineRunEvents` precisely because inlining it
 * would add ~375-625KB to every 5-second refetch of a 25-claim audit.
 *
 * Resolves to `[]` for a step event that recorded no candidates (pre-epic rows,
 * non-rag_retrieval steps); rejects with a 404 for an unknown stepEventId.
 */
export async function getPipelineStepEventRagRawCandidates(
  stepEventId: string,
): Promise<RagRawCandidateRow[]> {
  const result = await request<PipelineStepEventRagRawCandidatesResult>(
    `/internal/pipeline-step-events/${stepEventId}/rag-raw-candidates`,
  );
  return result.candidates;
}

// ---------------------------------------------------------------------------
// On-demand fetch of ONE interaction's archived full prompt body (CT8).
// ---------------------------------------------------------------------------

/**
 * The archived full prompt body for one LLM interaction. Call this ONLY
 * from a tester's expand handler, matching
 * `getPipelineStepEventRagRawCandidates`'s convention above — every other
 * view's `requestPrompt` is deliberately never the raw rendered prompt (a
 * truncated cataloged-prompt preview, or the fixed
 * "[uncataloged prompt...]" marker), so this is the only path to the real
 * text.
 *
 * Resolves to `{ available: false }` for an interaction with no archived
 * body (never archived, or the archive expired/failed to download);
 * rejects with a 404 for an unknown interactionId.
 */
export async function getLlmInteractionPromptArchive(
  interactionId: string,
): Promise<LlmInteractionPromptArchiveResult> {
  const data = await request<LlmInteractionPromptArchiveResult>(
    `/internal/llm-interactions/${interactionId}/prompt-archive`,
  );
  warnOnSchemaDrift(
    "getLlmInteractionPromptArchive",
    llmInteractionPromptArchiveResultSchema.safeParse(data),
  );
  return data;
}
